import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {
  Button, IconClose, TextInput, Icon, getTranslation,
} from '@resy/ui-kitchen';
import FocusTrap from 'focus-trap-react';
import pinSVG from '../../../../../../../ui-kitchen/src/svg/icons/ic_location_20x20.svg';
import EventsSearchList from '../EventsSearchList';
import './EventsSearchPillMenu.scss';
import { CITY_FILTER, FILTER_EVENTS } from '../../ExperientialEvents';

const EventsSearchPillMenu = (props) => {
  const {
    isOpen,
    locations,
    onClose,
    onLocationSelect,
    typeOfFilter,
    onFilterSelect,
    selectedFilterValues,
    onClickFilterEvents,
  } = props;

  const [state, setState] = React.useState({
    search: '',
    filteredLocations: [],
    interacted: false,
    menuAlignRight: false,
  });

  const cityPillMenuContainer = document.getElementsByClassName(`EventsSearchPillMenu__${typeOfFilter}`)[0];

  const onMenuOpen = () => {
    if (cityPillMenuContainer) {
      const {
        right: menuContainerPosRight,
        width: menuContainerWidth,
      } = cityPillMenuContainer.getBoundingClientRect();
      const isOffscreen = menuContainerPosRight <= menuContainerWidth;
      setState({
        ...state,
        menuAlignRight: isOffscreen,
      });
    }
  };

  const onMenuClose = () => {
    setState({
      ...state,
      menuAlignRight: false,
    });
  };

  useEffect(() => {
    if (!isOpen && state.interacted) {
      setState({ ...state, search: '', interacted: false });
    }
    if (isOpen) onMenuOpen();
    else onMenuClose();
  }, [isOpen]);

  const onChange = (e) => {
    const search = e.locationSearch;

    if (search.length === 0) {
      setState({
        ...state,
        filteredLocations: [],
        search,
      });
    } else if (search.trim() !== '') {
      const filtered = locations?.filter(
        (location) => {
          if (location.name.toLowerCase()
            .indexOf(search.trim().toLowerCase()) > -1) {
            return location;
          }
          return null;
        }
      );
      setState({
        ...state,
        filteredLocations: filtered.slice(0),
        search,
        interacted: true,
      });
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowUp') {
      document.querySelector('.EventsSearchList__list li:last-child').focus();
    } else if (e.key === 'ArrowDown') {
      document.querySelector('.EventsSearchList__list li:first-child').focus();
    } else if (e.key === 'Escape' && e.value !== '') {
      setState({ ...state, search: '' });
    }
  };

  let filterDataCombined = locations;
  if (typeOfFilter === CITY_FILTER) {
    filterDataCombined = (state.filteredLocations?.length && state.filteredLocations) || locations;
  }

  const locationSearchSection = () => (
    <>
      <div className="EventsSearchPillMenu__search-container">
        <Icon component={pinSVG} iconClass="pin" />
        <TextInput
          autocomplete="off"
          ariaAutocomplete="list"
          ariaControls="cities-list"
          name="locationSearch"
          aria-label="Search all locations"
          placeholder="Search all locations..."
          onChange={onChange}
          onKeyDown={onKeyDown}
          value={state.search}
        />

        <Button
          className="EventsSearchPillMenu__close-container"
          onClick={onClose}
          variant="close"
          aria-label={getTranslation({
            translationKey: 'close_menu',
            fallback: 'Close menu',
          })}
        >
          <IconClose background={false} />
        </Button>
      </div>

      <div className="EventsSearchList__error" aria-live="polite" role="status">
        {state.search.trim() !== '' && (state.filteredLocations?.length === 0) && (
        <>
          <p className="EventsSearchList__error-title">
            {`No results for “${state.search}”.`}
          </p>
          <p className="EventsSearchList__error-desc">
            Try another search, or select a City below.
          </p>
          <hr role="presentation" />
        </>
        )}
      </div>
      <EventsSearchList
        onLocationSelect={onLocationSelect}
        searchVal={state.search}
        locations={filterDataCombined}
        typeOfFilter={typeOfFilter}
        onFilterSelect={onFilterSelect}
        selectedFilterValues={selectedFilterValues}
      />
    </>
  );

  return (
    <FocusTrap
      active={isOpen}
      focusTrapOptions={{
        allowOutsideClick: true,
        clickOutsideDeactivates: true,
      }}
    >
      <div
        className={cx(`EventsSearchPillMenu EventsSearchPillMenu__${typeOfFilter}`, {
          'EventsSearchPillMenu--open': isOpen,
          'EventsSearchPillMenu--left': state.menuAlignRight,
        })}
      >
        {(typeOfFilter === CITY_FILTER) && locationSearchSection()}
        {(typeOfFilter === FILTER_EVENTS) && (
          <>
            <div className="EventsSearchPillMenu__search-container">
              <Button
                className="EventsSearchPillMenu__close-container"
                onClick={onClose}
                variant="close"
                aria-label={getTranslation({
                  translationKey: 'close_menu',
                  fallback: 'Close menu',
                })}
              >
                <IconClose background={false} />
              </Button>
            </div>
            <EventsSearchList
              onLocationSelect={onLocationSelect}
              searchVal={state.search}
              locations={filterDataCombined}
              typeOfFilter={typeOfFilter}
              onFilterSelect={onFilterSelect}
              selectedFilterValues={selectedFilterValues}
              onClickFilterEvents={onClickFilterEvents}
            />
          </>
        )}

      </div>
    </FocusTrap>
  );
};

EventsSearchPillMenu.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  locations: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    })
  ).isRequired,
  onClose: PropTypes.func.isRequired,
  onLocationSelect: PropTypes.func.isRequired,
  typeOfFilter: PropTypes.string.isRequired,
  onFilterSelect: PropTypes.func.isRequired,
  selectedFilterValues: PropTypes.shape({}).isRequired,
  onClickFilterEvents: PropTypes.func.isRequired,
};

export default EventsSearchPillMenu;
